import { calcMargin, netToAud } from '../packageOptions/marginRules';
import { BASIC_CATEGORY_INDEX } from '../packageOptions/packageCategories';
import type { OptionState } from '../packageOptions/types';
import { computeDailyDiscountStats } from '../scrape/otaParity/computeGenuineDiscount';
import { type ScrapePrvMonthRow, computeScrapePrvByMonth } from '../scrape/otaParity/computeScrapePrv';
import { mean } from '../scrape/otaParity/computeSummaryStats';
import type { DailyDiscountStats, DailyMarketStats, ParsedOtaCsv, StayInputOverrides } from '../scrape/otaParity/types';

// Room before stay: taking the stay first lets a pricier room that alone sells a shorter stay lead.
export function leadNights(option: OptionState | undefined): number {
  for (const room of option?.roomsByCategory[BASIC_CATEGORY_INDEX] ?? []) {
    const priced = option.lengthsOfStay.filter(
      (nights) => Number(option.pricingByRoomId[room.id]?.sellByLos[nights]) > 0,
    );
    if (priced.length) {
      return Math.min(...priced);
    }
  }
  return 0;
}

export type Lead = { nights: number; sell: number; net: number | undefined; roomAssetId: string };

export function leadOf(option: OptionState | undefined): Lead | null {
  const nights = leadNights(option);
  const lead = leadSellAndNet(option, nights);
  return lead ? { nights, ...lead } : null;
}

// PRV compares the lead with OTA prices for the scrape's stay, so it only reads a scrape run at the lead's stay.
export function scrapeStayFor(lead: Lead | null, stay: StayInputOverrides | null): StayInputOverrides | null {
  return stay?.nights === lead?.nights ? stay : null;
}

export function leadSellAndNet(
  option: OptionState | undefined,
  nights: number,
): { sell: number; net: number | undefined; roomAssetId: string } | null {
  if (!option || nights <= 0) {
    return null;
  }

  const rooms = option.roomsByCategory[BASIC_CATEGORY_INDEX] ?? [];
  for (const room of rooms) {
    const pricing = option.pricingByRoomId[room.id];
    const sell = Number(pricing?.sellByLos[nights]);
    if (sell > 0) {
      const rawNet = pricing?.netByLos[nights];
      const parsedNet = rawNet === undefined || rawNet === '' ? Number.NaN : Number(rawNet);
      return { sell, net: Number.isFinite(parsedNet) ? parsedNet : undefined, roomAssetId: room.assetId };
    }
  }

  return null;
}

export function stayInputsForLeadSell(
  stay: StayInputOverrides,
  sellAud: number,
  prvInclusionsAud: number,
): StayInputOverrides {
  return {
    ...stay,
    baseSellPriceAud: sellAud,
    prvInclusionsAud,
    prvInclusionsNative: stay.currencyCode === 'AUD' ? null : stay.prvInclusionsNative,
  };
}

export function averageDailyPct(values: Array<number | null | undefined>): number | null {
  return mean(values.filter((value): value is number => value != null));
}

export function weightedGenuineDiscountPct(discountStats: Array<DailyDiscountStats>): number | null {
  let numerator = 0;
  let denominator = 0;
  for (const row of discountStats) {
    if (row.prvPlusMin != null && row.prvPlusMin > 0 && row.sellDisplay != null && row.sellDisplay > 0) {
      numerator += row.prvPlusMin - row.sellDisplay;
      denominator += row.prvPlusMin;
    }
  }
  return denominator > 0 ? (numerator / denominator) * 100 : null;
}

export interface OptionPricingSummary {
  optionIndex: number;
  sell: number | null;
  net: number | null;
  marginPct: number | null;
  prvPct: number | null;
  monthRows: Array<ScrapePrvMonthRow>;
}

export function computeOptionPricingSummary({
  optionIndex,
  option,
  stay,
  parsedCsv,
  marketStats,
  discountStats,
  netCurrency = 'AUD',
  audToSourceCurrencyRate = { AUD: 1 },
}: {
  optionIndex: number;
  option?: OptionState;
  // The scrape's stay when it matches the lead's (scrapeStayFor), else null.
  stay: StayInputOverrides | null;
  parsedCsv: ParsedOtaCsv | null;
  marketStats: Array<DailyMarketStats>;
  discountStats: Array<DailyDiscountStats>;
  netCurrency?: string;
  audToSourceCurrencyRate?: Record<string, number | undefined>;
}): OptionPricingSummary {
  const lead = leadOf(option);
  const netAud = lead?.net != null ? netToAud(lead.net, netCurrency, audToSourceCurrencyRate) : undefined;
  const margin = lead && netAud != null ? calcMargin(lead.sell, netAud) : undefined;

  return {
    optionIndex,
    sell: lead?.sell ?? null,
    net: netAud ?? null,
    marginPct: margin?.pct ?? null,
    prvPct: weightedGenuineDiscountPct(discountStats),
    monthRows: parsedCsv && stay ? computeScrapePrvByMonth(parsedCsv, marketStats, discountStats) : [],
  };
}

export function computeOptionDiscountStats(
  marketStats: Array<DailyMarketStats>,
  stay: StayInputOverrides | null,
  sellAud: number | null,
  prvInclusionsAud: number,
  stayNightlySurchargeByDate: Record<string, number>,
): Array<DailyDiscountStats> {
  if (!stay || sellAud == null) {
    return [];
  }

  return computeDailyDiscountStats(marketStats, {
    ...stayInputsForLeadSell(stay, sellAud, prvInclusionsAud),
    stayNightlySurchargeByDate,
  });
}
